import { useCallback, type MutableRefObject } from "react";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import {
  LiveFlashKind,
  TrackedMemberField,
} from "@/types/frontend-types";
import { deriveMemberDisplayRole } from "@/utils/sessionMemberPresentation";
import type {
  FlagLiveFlash,
  FlagMemberChangesInput,
} from "./session-live-signals.types";

interface UseSessionMemberChangeLiveSignalsInput {
  membersRef: MutableRefObject<Record<string, Member>>;
  flagLiveFlash: FlagLiveFlash;
}

export function useSessionMemberChangeLiveSignals({
  membersRef,
  flagLiveFlash,
}: UseSessionMemberChangeLiveSignalsInput): (
  input: FlagMemberChangesInput,
) => void {
  return useCallback(
    (input: FlagMemberChangesInput): void => {
      if (!input.canFlash) return;
      input.items.forEach((nextMember) => {
        const previousMember = membersRef.current[nextMember.id];
        if (!previousMember) return;

        const fields = selectChangedMemberFields({
          previousMember,
          nextMember,
        });
        if (fields.length === 0) return;
        flagLiveFlash({
          kind: LiveFlashKind.Member,
          entityId: nextMember.id,
          fields,
        });
      });
    },
    [flagLiveFlash, membersRef],
  );
}

function selectChangedMemberFields(input: {
  previousMember: Member;
  nextMember: Member;
}): TrackedMemberField[] {
  const fields: TrackedMemberField[] = [];
  if (input.previousMember.name !== input.nextMember.name) {
    fields.push(TrackedMemberField.Name);
  }
  if (
    deriveMemberDisplayRole(input.previousMember) !==
    deriveMemberDisplayRole(input.nextMember)
  ) {
    fields.push(TrackedMemberField.Role);
  }
  return fields;
}
